import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '../../../environments/environment';

export interface InstructorPlan {
  _id: string;
  name: string;
  slug: string;
  description?: string;
  platformFeeRate: number;
  billingCycleMonths: number;
  price: number;
  currency: string;
  quotas: Record<string, number>;
  isPublic: boolean;
  isActive: boolean;
  isNewSignupDefault: boolean;
  sortOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

export type PlanAssignmentStatus = 'ACTIVE' | 'EXPIRED' | 'CANCELLED';

export interface PlanAssignment {
  _id: string;
  instructor: {
    _id: string;
    username: string;
    profile?: { personal?: { firstName?: string; lastName?: string } };
  };
  plan: InstructorPlan;
  status: PlanAssignmentStatus;
  assignedBy?: { _id: string; username: string } | null;
  assignedAt: string;
  expiresAt?: string | null;
  isTrialPlan: boolean;
  trialEndsAt?: string | null;
}

export interface PaginatedResponse<T> {
  docs: T[];
  totalDocs: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

@Injectable({ providedIn: 'root' })
export class InstructorPlanApiService {
  private http = inject(HttpClient);
  private base = `${environment.host}/api/v1/instructor-plans`;

  // ── Instructor (self) ──────────────────────────────────────────────────────

  getMyPlan() {
    return this.http.get<PlanAssignment | null>(`${this.base}/my-plan`);
  }

  // ── Plans ──────────────────────────────────────────────────────────────────

  getAdminPlans(page = 1, limit = 20) {
    return this.http.get<PaginatedResponse<InstructorPlan>>(
      `${this.base}/admin?page=${page}&limit=${limit}`
    );
  }

  createPlan(dto: Partial<InstructorPlan>) {
    return this.http.post<InstructorPlan>(`${this.base}/admin`, dto);
  }

  updatePlan(id: string, dto: Partial<InstructorPlan>) {
    return this.http.put<InstructorPlan>(`${this.base}/admin/${id}`, dto);
  }

  // ── Assignments ────────────────────────────────────────────────────────────

  getAllAssignments(page = 1, limit = 20, status?: string) {
    let url = `${this.base}/admin/assignments?page=${page}&limit=${limit}`;
    if (status) url += `&status=${status}`;
    return this.http.get<PaginatedResponse<PlanAssignment>>(url);
  }

  getAssignmentsByPlan(planId: string, page = 1, limit = 20) {
    return this.http.get<PaginatedResponse<PlanAssignment>>(
      `${this.base}/admin/${planId}/assignments?page=${page}&limit=${limit}`
    );
  }

  getAssignmentsByInstructor(instructorId: string, page = 1, limit = 20) {
    return this.http.get<PaginatedResponse<PlanAssignment>>(
      `${this.base}/admin/instructor/${instructorId}/assignments?page=${page}&limit=${limit}`
    );
  }

  getActiveAssignmentForInstructor(instructorId: string) {
    return this.http.get<PlanAssignment | null>(
      `${this.base}/admin/instructor/${instructorId}/active-assignment`
    );
  }

  assignPlanToInstructor(planId: string, instructorUsername: string) {
    return this.http.post<PlanAssignment>(
      `${this.base}/admin/${planId}/assign`,
      {
        instructorUsername,
      }
    );
  }

  cancelAssignment(assignmentId: string) {
    return this.http.post<PlanAssignment>(
      `${this.base}/admin/assignments/${assignmentId}/cancel`,
      {}
    );
  }
}
